import { useEffect, useRef } from 'react';

import { useFormActions, useFormValues } from '../core';
import type { FormInitialValues, SingleFormField } from '../typings';
import {
  getClearedFieldValue,
  shouldClearFieldValue,
} from '../utils/clearedFieldValue';

/**
 * True while the boolean field governing this one is switched on, and wipes this
 * field's value on the off→on transition so no stale amount is ever submitted.
 *
 * Unlike Not Applicable, nothing server-side clears on the client's behalf during
 * the same tick, so this write goes through the normal change handler and autosaves.
 */
export const useDisabledWhenToggled = (field: SingleFormField): boolean => {
  const values = useFormValues<FormInitialValues>();
  const { setFieldValue } = useFormActions();

  const toggleFieldId = field.disabledWhenFieldId;
  const isToggledOn = toggleFieldId ? values[toggleFieldId] === true : false;

  // Seeded from the current state so a form opened with the toggle already on never writes.
  const wasToggledOn = useRef(isToggledOn);
  const valuesRef = useRef(values);

  // Formik returns a fresh values object on every keystroke anywhere in the form. Mirroring it
  // into a ref keeps it out of the dependency array below, so the clearing effect runs when the
  // toggle flips rather than on every edit in the section.
  useEffect(() => {
    valuesRef.current = values;
  });

  useEffect(() => {
    const justToggledOn = isToggledOn && !wasToggledOn.current;
    wasToggledOn.current = isToggledOn;

    if (!justToggledOn) return;

    const clearedValue = getClearedFieldValue(field);

    if (!shouldClearFieldValue(clearedValue, valuesRef.current[field.fieldId]))
      return;

    // The amount is no longer required while the toggle is on, so revalidating
    // mid-flip would flash a required error.
    setFieldValue(field.fieldId, clearedValue, false);
  }, [isToggledOn, field, setFieldValue]);

  return isToggledOn;
};
